import { Key, memo, useCallback, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatNome, formatSingularOrPlural } from '@/utils/format'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import ButtonConfirm from '@/components/ui/button-confirm-delet'
import { useDeleteManyUser } from '../services/useDeleteManyUsers'
import { useToast } from '@/lib/hooks/use-toast'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

interface User {
  id?: string
  cpf?: string
  name?: string
  email?: string
  typeUser?: string
  typeCargo?: string
  id_tenant?: string
  nome?: string
  cnpjcpf?: string
  typeCategoria?: string
  status?: string
}

interface UsersTableProps {
  users: User[]
  onClickUser: (user: User) => void
}

export default memo(function UsersTable({ users, onClickUser }: UsersTableProps) {
  const [checked, setChecked] = useState<User[]>([])
  const { mutate } = useDeleteManyUser()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const handleCheckedChange = (checked: boolean, user: User) => {
    if (checked) {
      setChecked(prev => {
        if (prev.find(p => p.id === user.id)) return prev
        return [...prev, user]
      })
    } else {
      setChecked(prev => {
        return prev.filter(p => p.id !== user.id)
      })
    }
  }

  const handleDelete = useCallback(() => {
    mutate(
      checked.map(c => c.id!),
      {
        onSuccess: () => {
          queryClient.refetchQueries({
            queryKey: ['gerente']
          })
          queryClient.refetchQueries({
            queryKey: ['user', 'tenant']
          })
          toast({
            title: 'Sucesso!',
            description: 'Você deletou com sucessos os usários'
          })
        },
        onError: error => {
          console.error('Erro ao excluir usuário:', error)
          const message = error instanceof AxiosError ? error.response?.data.message : 'Erro ao tentar deletar usuário.'
          toast({
            title: 'Erro',
            description: message,
            variant: 'destructive'
          })
        }
      }
    )
  }, [checked])

  if (users.length === 0) {
    return (
      <div className="text-center text-gray-500 py-10">Nenhum usuário encontrado com os critérios selecionados.</div>
    )
  }

  return (
    <>
      <div className={`w-full py-2 px-1 transition-all duration-300 transform ${checked.length > 0 ? '' : 'hidden '}`}>
        <p className="text-xs text-primary">
          {' '}
          {checked.length} {formatSingularOrPlural('item', 'itens', checked.length)} selecionado{' '}
        </p>

        <ButtonConfirm
          confirmText="Deseja excluir todos os usuários selecionados?"
          onSuccess={handleDelete}
          successBtnText="Excluir"
        />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead></TableHead>
            <TableHead className="w-[200px] md:w-[250px]">Nome</TableHead>
            <TableHead className="hidden md:table-cell">Email</TableHead>
            <TableCell className="md:table-cell text-center">Status</TableCell>
            <TableHead className="hidden lg:table-cell">Cargo</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user: User, index: Key | null | undefined) => (
            <TableRow
              key={user.id || index}
              className="cursor-pointer hover:bg-muted transition"
              onClick={() => onClickUser(user)}
            >
              <TableCell>
                <Checkbox
                  name={user.id}
                  onClick={event => event.stopPropagation()}
                  onCheckedChange={checked => handleCheckedChange(Boolean(checked), user)}
                />
              </TableCell>
              <TableCell className="font-medium">{formatNome(user.name || '')}</TableCell>
              <TableCell className="hidden text-center md:table-cell">{user.email}</TableCell>
              <TableCell className="md:table-cell text-center">
                {user.status === 'Ativo' && <Badge variant="outline">Ativo</Badge>}

                {user.status === 'Pendente' && <Badge variant="outline">Pendente</Badge>}
              </TableCell>
              <TableCell className="hidden lg:table-cell text-center">{formatNome(user.typeCargo || '')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  )
})
